import { useId, useMemo, useState } from 'react';
import type { Activity } from '../../schemas';
import { decimalFormat, numberFormat } from '../../domain/trail';
import { ChartFrame, ChartTooltip, EmptyAlbum } from '../../styles';

const elevationPlot = { top: 10, right: 8, bottom: 30, left: 42 } as const;

export function ElevationChart({
  activity,
  highlightedLocation,
  onLocationChange,
}: {
  activity: Activity;
  highlightedLocation: [number, number] | null;
  onLocationChange: (coordinate: [number, number] | null) => void;
}) {
  const gradientId = useId().replaceAll(':', '');
  const profile = activity.elevation_profile;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const width = 360;
  const height = 164;

  const drawing = useMemo(() => {
    if (profile.length < 2) return null;
    const maxDistance = Math.max(...profile.map(([distance]) => distance), 1);
    const elevations = profile.map(([, elevation]) => elevation);
    const minimum = Math.min(...elevations);
    const maximum = Math.max(...elevations);
    const range = Math.max(maximum - minimum, 1);
    const plotWidth = width - elevationPlot.left - elevationPlot.right;
    const plotHeight = height - elevationPlot.top - elevationPlot.bottom;
    const coordinates = profile.map(
      ([distance, elevation]) =>
        [
          elevationPlot.left + (distance / maxDistance) * plotWidth,
          elevationPlot.top + ((maximum - elevation) / range) * plotHeight,
        ] as const,
    );
    const line = `M ${coordinates.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ')}`;
    const baseline = height - elevationPlot.bottom;
    const area = `${line} L ${coordinates.at(-1)?.[0] ?? width} ${baseline} L ${coordinates[0]![0]} ${baseline} Z`;
    return {
      line,
      area,
      coordinates,
      maximum,
      minimum,
      maxDistance,
      xTicks: [0, maxDistance / 2, maxDistance],
      yTicks: [minimum, minimum + range / 2, maximum],
    };
  }, [profile]);
  const highlightedIndex = useMemo(() => {
    if (!highlightedLocation || !profile.length) return null;
    const latitudeScale = Math.cos((highlightedLocation[1] * Math.PI) / 180);
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    profile.forEach(([, , lon, lat], index) => {
      const x = (lon - highlightedLocation[0]) * latitudeScale;
      const y = lat - highlightedLocation[1];
      const distance = x * x + y * y;
      if (distance < nearestDistance) {
        nearestIndex = index;
        nearestDistance = distance;
      }
    });
    return nearestIndex;
  }, [highlightedLocation, profile]);
  const displayIndex = hoveredIndex ?? highlightedIndex;

  if (!drawing) return <EmptyAlbum>Altitude samples weren’t present in this recording.</EmptyAlbum>;

  return (
    <ChartFrame
      onPointerMove={(event) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        const svgX = ((event.clientX - bounds.left) / bounds.width) * width;
        const progress = Math.max(
          0,
          Math.min(1, (svgX - elevationPlot.left) / (width - elevationPlot.left - elevationPlot.right)),
        );
        const index = Math.round(progress * (profile.length - 1));
        if (index !== hoveredIndex) {
          setHoveredIndex(index);
          const point = profile[index];
          onLocationChange(point ? [point[2], point[3]] : null);
        }
      }}
      onPointerLeave={() => {
        setHoveredIndex(null);
        onLocationChange(null);
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Elevation profile for ${activity.title}. Move across the chart for exact values.`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#ff6a2a" stopOpacity="0.42" />
            <stop offset="1" stopColor="#ff6a2a" stopOpacity="0.03" />
          </linearGradient>
        </defs>
        {drawing.yTicks.map((tick) => {
          const y =
            elevationPlot.top +
            ((drawing.maximum - tick) / Math.max(drawing.maximum - drawing.minimum, 1)) *
              (height - elevationPlot.top - elevationPlot.bottom);
          return (
            <g key={`y-${tick}`}>
              <line
                x1={elevationPlot.left}
                x2={width - elevationPlot.right}
                y1={y}
                y2={y}
                stroke="#314738"
                strokeOpacity="0.13"
              />
              <text x={elevationPlot.left - 6} y={y + 3} fill="#62645d" fontSize="8" textAnchor="end">
                {numberFormat.format(Math.round(tick))}
              </text>
            </g>
          );
        })}
        {drawing.xTicks.map((tick) => {
          const x =
            elevationPlot.left +
            (tick / drawing.maxDistance) * (width - elevationPlot.left - elevationPlot.right);
          return (
            <g key={`x-${tick}`}>
              <line
                x1={x}
                x2={x}
                y1={height - elevationPlot.bottom}
                y2={height - elevationPlot.bottom + 4}
                stroke="#314738"
                strokeOpacity="0.5"
              />
              <text
                x={x}
                y={height - elevationPlot.bottom + 14}
                fill="#62645d"
                fontSize="8"
                textAnchor={tick === 0 ? 'start' : tick === drawing.maxDistance ? 'end' : 'middle'}
              >
                {decimalFormat.format(tick)}
              </text>
            </g>
          );
        })}
        <line
          x1={elevationPlot.left}
          x2={elevationPlot.left}
          y1={elevationPlot.top}
          y2={height - elevationPlot.bottom}
          stroke="#314738"
          strokeOpacity="0.42"
        />
        <line
          x1={elevationPlot.left}
          x2={width - elevationPlot.right}
          y1={height - elevationPlot.bottom}
          y2={height - elevationPlot.bottom}
          stroke="#314738"
          strokeOpacity="0.42"
        />
        <text
          x="9"
          y={elevationPlot.top + (height - elevationPlot.top - elevationPlot.bottom) / 2}
          fill="#4f564d"
          fontSize="8"
          letterSpacing="0.08em"
          textAnchor="middle"
          transform={`rotate(-90 9 ${elevationPlot.top + (height - elevationPlot.top - elevationPlot.bottom) / 2})`}
        >
          ELEVATION (M)
        </text>
        <text
          x={elevationPlot.left + (width - elevationPlot.left - elevationPlot.right) / 2}
          y={height - 3}
          fill="#4f564d"
          fontSize="8"
          letterSpacing="0.08em"
          textAnchor="middle"
        >
          DISTANCE (KM)
        </text>
        <path d={drawing.area} fill={`url(#${gradientId})`} />
        <path
          d={drawing.line}
          fill="none"
          stroke="#d94e16"
          strokeWidth="2.5"
          vectorEffect="non-scaling-stroke"
        />
        {displayIndex !== null && drawing.coordinates[displayIndex] && (
          <>
            <line
              x1={drawing.coordinates[displayIndex][0]}
              x2={drawing.coordinates[displayIndex][0]}
              y1={elevationPlot.top}
              y2={height - elevationPlot.bottom}
              stroke="#314738"
              strokeDasharray="3 3"
              strokeOpacity="0.55"
            />
            <circle
              cx={drawing.coordinates[displayIndex][0]}
              cy={drawing.coordinates[displayIndex][1]}
              r="4.5"
              fill="#eee9d9"
              stroke="#d94e16"
              strokeWidth="2.5"
            />
          </>
        )}
      </svg>
      {displayIndex !== null && profile[displayIndex] && drawing.coordinates[displayIndex] && (
        <ChartTooltip
          $x={(drawing.coordinates[displayIndex][0] / width) * 100}
          $y={(drawing.coordinates[displayIndex][1] / height) * 100}
        >
          <strong>{numberFormat.format(profile[displayIndex][1])} m</strong>
          {decimalFormat.format(profile[displayIndex][0])} km
        </ChartTooltip>
      )}
    </ChartFrame>
  );
}
